import React from 'react';
import { Calendar, Clock, Users, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';


export default function Scheduling() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#9B1421]/20 to-[#7A0F1A]/10 py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="inline-block bg-[#9B1421]/20 p-4 rounded-full mb-6">
              <Calendar className="w-12 h-12 text-rollops-accent mx-auto" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Class Scheduling That <span className="text-rollops-accent">Saves Time</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#A9A3A3] mb-8 max-w-2xl mx-auto">
              Create schedules in minutes. Track attendance automatically. Keep your members engaged.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                Remember the last time you had to reschedule a class? You probably spent 20 minutes calling 
                or texting members, updating your whiteboard, and hoping everyone got the message. 
                There&apos;s got to be a better way.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                RollOps makes class scheduling simple. Create your schedule once, and it repeats automatically. 
                Members can see upcoming classes, enroll in advance, and get automatic reminders. 
                You can track attendance with a tap, and see who&apos;s coming to each class at a glance.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                When life happens and you need to reschedule? One click updates everyone automatically. 
                No more phone tag. No more confusion. Just smooth, professional communication that keeps 
                your members coming back.
              </p>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-20 px-4 bg-[#1A1818]">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Scheduling Made Simple
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Calendar className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Recurring Schedules</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Set up your weekly schedule once, and it repeats automatically. Add special events, 
                      cancel individual classes, or modify the whole schedule - all with a few clicks.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Users className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Class Enrollment</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Members can enroll in classes in advance. See who&apos;s coming, manage waitlists, 
                      and set capacity limits. Know exactly how many members to expect for each class.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Clock className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Automatic Attendance</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Track attendance automatically with geofencing, or check members in manually. 
                      See attendance patterns, identify members who haven&apos;t been in recently, 
                      and celebrate your regulars.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Bell className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Smart Notifications</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Send automatic reminders before classes. Notify members of schedule changes instantly. 
                      Keep everyone in the loop without lifting a finger.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stop Wasting Time on Scheduling
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-8">
              Join gym owners who are already saving hours every week with RollOps.
            </p>
            <Link
              to="/founders-promo"
              className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Get Started Today
            </Link>
          </div>
        </section>
      </main>

    </div>
  );
}
